
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { getTranslationForLocale } from '@/utils/i18n-helpers';
import { decodeString } from '@/utils/decoding';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import SelectComponent from '@/components/shared/Select.vue';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';

const namespace = 'auth';

@Component({
	components: {
		SelectComponent,
		StandardModal,
		ToggleSwitch
	}
})
export default class PauseOrdersModal extends Vue {
	@Action('toggleOrdersPaused', { namespace }) private toggleOrdersPaused!: (payload?: { message?: OrdersPausedMessage, deleteMessage?: boolean, locationId?: string }) => Promise<void>;
	@Getter('getRestaurant', { namespace }) private currentRestaurant!: Restaurant;
	@Prop({ type: Object, required: false, default: null }) private targetRestaurant!: Tenant | Restaurant | null;
	@Prop({ type: String, required: false, default: '' }) private locationId!: string;

	private messageObj: OrdersPausedMessage = {} as OrdersPausedMessage;
	private selectedLocale: RestaurantLocale = {} as RestaurantLocale;
	private useCustomMessage: boolean = false;
	private loading: boolean = false;
	private ordersPaused: boolean = false; // Local state to prevent modal content from changing while loading

	/**
	 * Active restaurant the modal is operating on. Uses the explicit target when provided
	 * (list-initiated usage), otherwise falls back to the current tenant (OrderManagement usage).
	 *
	 * @return {Tenant | Restaurant}
	 */
	private get restaurant(): Tenant | Restaurant {
		return this.targetRestaurant || this.currentRestaurant;
	}

	/**
	 * Check if default locale is selected, used to determine which input to show
	 *
	 * @return {boolean}
	 */
	private get isDefaultLocale(): boolean {
		return !this.selectedLocale || !Object.keys(this.selectedLocale).length || (this.selectedLocale && this.selectedLocale.is_default);
	}

	/**
	 * Get the default locale short code
	 *
	 * @return {string}
	 */
	private get defaultLocaleShort(): string {
		return this.restaurant.locales?.find((locale: RestaurantLocale) => locale.is_default)?.locale_short || 'en';
	}

	private get modalTitle(): string {
		return this.ordersPaused ? this.$t('orders.management.pause_orders_modal.unpause_title') : this.$t('orders.management.pause_orders_modal.title');
	}

	private get confirmButtonText(): string {
		return this.ordersPaused ? this.$t('shared.actions_array.btn_continue') : this.$t('orders.management.pause_orders_modal.confirm_btn');
	}

	private created() {
		this.ordersPaused = this.restaurant.orders_paused;
		this.setLocaleToDefault();
		this.setInitialMessage();
		this.useCustomMessage = !this.allLocalesUsingDefaultMessage();
	}

	/**
	 * Switch to default locale. Uses a defensive copy for the sort so we don't mutate
	 * the Vuex-backed locales array in place (the modal can be pointed at a Tenant from
	 * state.restaurants via the targetRestaurant prop).
	 *
	 * @return {void}
	 */
	private setLocaleToDefault(): void {
		if (this.restaurant.locales?.length > 1) {
			const sortedLocales = [...this.restaurant.locales].sort((a: RestaurantLocale, b: RestaurantLocale) => Number(b.is_default) - Number(a.is_default));
			this.selectedLocale = sortedLocales.find((locale: RestaurantLocale) => locale.is_default)!;
		}
	}

	/**
	 * Set initial paused orders message for all locales from order options
	 * If there is no message set, use the default message
	 *
	 * @return {void}
	 */
	private setInitialMessage(): void {
		this.messageObj.orders_paused_message = decodeString(this.restaurant.order_options?.orders_paused_message) || getTranslationForLocale('orders.management.pause_orders_modal.default_message', this.defaultLocaleShort);

		if (this.restaurant.locales?.length > 1) {
			this.messageObj.localization = {};

			for (const locale of this.restaurant.locales) {
				if (!locale.is_default) {
					this.messageObj.localization![locale.locale_short] = {
						orders_paused_message: decodeString(this.restaurant.order_options?.localization?.[locale.locale_short].orders_paused_message) || getTranslationForLocale('orders.management.pause_orders_modal.default_message', locale.locale_short)
					};
				}
			}
		}
	}

	/**
	 * Check if all locales are using the default message
	 *
	 * @return {boolean}
	 */
	private allLocalesUsingDefaultMessage(): boolean {
		const defaultLocaleUsingDefault = this.messageObj.orders_paused_message === getTranslationForLocale('orders.management.pause_orders_modal.default_message', this.defaultLocaleShort);
		if (!this.restaurant.locales) {
			return defaultLocaleUsingDefault;
		}
		const allLocalesUsingDefault = this.restaurant.locales?.every((locale: RestaurantLocale) => {
			// Skip check for the default locale, will always be undefined in the localization object
			return locale.is_default || (this.messageObj.localization?.[locale.locale_short]?.orders_paused_message === getTranslationForLocale('orders.management.pause_orders_modal.default_message', locale.locale_short));
		});

		return defaultLocaleUsingDefault && allLocalesUsingDefault;
	}

	/**
	 * Reset the custom message to the default message when the switch is turned off
	 *
	 * @return {void}
	 */
	private resetCustomMessage(): void {
		if (!this.useCustomMessage) {
			this.messageObj.orders_paused_message = getTranslationForLocale('orders.management.pause_orders_modal.default_message', this.defaultLocaleShort);

			if (this.restaurant.locales?.length > 1) {
				this.messageObj.localization = {};

				for (const locale of this.restaurant.locales) {
					if (!locale.is_default) {
						this.messageObj.localization![locale.locale_short] = {
							orders_paused_message: getTranslationForLocale('orders.management.pause_orders_modal.default_message', locale.locale_short)
						};
					}
				}
			}
		}
	}

	/**
	 * Toggle the orders paused state and send the message ob
	 *
	 * @return {Promise<void>}
	 */
	private async togglePauseOrders(): Promise<void> {
		// Fallback to the default message for any locale that doesn't have a message
		if (this.useCustomMessage) {
			if (!this.messageObj.orders_paused_message) {
				this.messageObj.orders_paused_message = getTranslationForLocale('orders.management.pause_orders_modal.default_message', this.defaultLocaleShort);
			}
			if (this.messageObj.localization) {
				Object.keys(this.messageObj.localization).forEach((locale: string) => {
					if (!this.messageObj.localization![locale].orders_paused_message) {
						this.messageObj.localization![locale].orders_paused_message = getTranslationForLocale('orders.management.pause_orders_modal.default_message', locale);
					}
				});
			}
		}

		try {
			this.loading = true;
			// Build the payload. Include locationId when the modal is targeting a non-current location.
			// Include message/deleteMessage only when pausing (the unpause path doesn't touch the message).
			const togglePayload: { message?: OrdersPausedMessage, deleteMessage?: boolean, locationId?: string } = {};
			if (this.locationId) {
				togglePayload.locationId = this.locationId;
			}
			if (!this.ordersPaused) {
				togglePayload.message = this.messageObj;
				togglePayload.deleteMessage = !this.useCustomMessage || this.allLocalesUsingDefaultMessage();
			}
			await this.toggleOrdersPaused(togglePayload);

			this.$emit('confirm');
			this.loading = false;
			this.ordersPaused = this.restaurant.orders_paused;
		}
		catch (errorMessage) {
			this.loading = false;
			Toast.error(this.$t('orders.management.error_pausing_orders', {
				errorMessage,
				action: (this.ordersPaused
					? this.$t('orders.management.unpause_orders_btn')
					: this.$t('orders.management.pause_orders_btn'))
			}));
		}
	}
}
